import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from "react-hook-form";
import { 
  SelectChangeEvent, 
  Autocomplete, 
  TextField, 
  Select, 
  MenuItem, 
  IconButton,
  Box,
  Card
} from "@mui/material";
import GroupedCategorySelect from '../FormFields/GroupedCategorySelect';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Refresh } from "@mui/icons-material";
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import useModuleAccess from '../../hooks/useModuleAccess';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { makeStyles } from "@material-ui/core/styles";
import { fetchAccountsByName, fetchBuyerOrSupplierByName } from "../../store/accountThunk";
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { Utils } from '../../utils/common-utils';
import Filters from '../Filters/Filters';
import { ExpenseFilters, ExpenseCategory } from '../../utils/expense.types';
import { styled } from '@mui/material/styles';

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
});

const StyledCard = styled(Card)(({ theme }) => ({
  padding: '12px 16px',
  marginBottom: 16,
  boxShadow: 'none',
  border: '1px solid #e9ecf1',
  borderRadius: 8,
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap'
}));

interface ExpenseFilterProps {
  onFiltersChange: (filters: ExpenseFilters) => void;
  onReset?: () => void;
  currentFilters: ExpenseFilters;
  categories?: ExpenseCategory[];
  hideVendorSearch?: boolean;
  defaultAllDates?: boolean;
}

const ExpenseFilter: FC<ExpenseFilterProps> = ({ onFiltersChange, onReset, currentFilters, categories = [], hideVendorSearch = false, defaultAllDates = false }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const canViewSuppliers = useModuleAccess().canAccessModule('suppliers');
  const { selectedAccount, modulePermissions } = useAppSelector(state => state.auth);
  
  // Date range state
  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();
  // Filled by Filters on mount with its default (Last 30 Days) unless defaultAllDates is true.
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null
  });
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState(defaultAllDates ? "all" : "Last 30 Days");
  const [showDateRange, setShowDateRange] = useState(false);
  
  // Supplier/Vendor state
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [vendorSearchValue, setVendorSearchValue] = useState("");
  
  // Other filters
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("all");
  
  const methods = useForm<any>({});

  // Status options
  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Booked', value: 'BOOKED' },
    { label: 'Recorded', value: 'EXPENSE_RECORDED' },
  ];

  // Payment mode options
  const paymentModeOptions = [
    { label: 'All', value: 'all' },
    { label: 'Cash', value: 'CASH' },
    { label: 'Cheque', value: 'CHEQUE' },
    { label: 'Card', value: 'CARD' },
    { label: 'UPI', value: 'UPI' },
    { label: 'Net Banking', value: 'NETBANKING' },
  ];

  // No need for categoryOptions anymore - GroupedCategorySelect handles this

  // Create and apply filters
  const createFilter = () => {
    const filters: ExpenseFilters = {};

    if (selectedVendor && selectedVendor._id !== 'all') {
      if (selectedVendor._type === 'employee') {
        filters.employee = selectedVendor._id;
      } else {
        filters.vendor = selectedVendor._id;
      }
    }
    
    if (selectedStatus && selectedStatus !== 'all') {
      filters.paymentStatus = selectedStatus;
    }
    
    if (selectedCategory && selectedCategory !== 'all') {
      // selectedCategory now contains the category ID from GroupedCategorySelect
      filters.expenseCategory = selectedCategory;
    }
    
    if (selectedPaymentMode && selectedPaymentMode !== 'all') {
      filters.paymentMode = selectedPaymentMode;
    }
    
    if (dateRange.startDate) {
      filters.dateFrom = dateRange.startDate.toISOString();
      filters.startDate = dateRange.startDate;
    }
    
    if (dateRange.endDate) {
      filters.dateTo = dateRange.endDate.toISOString();
      filters.endDate = dateRange.endDate;
    }
    
    onFiltersChange(filters);
  };

  // Reset filters
  const onResetClickHandler = () => {
    setSelectedVendor(null);
    setVendorSearchValue('');
    setSelectedStatus("all");
    setSelectedCategory("all"); // This will map to empty string in the component
    setSelectedPaymentMode("all");
    if (defaultAllDates) {
      setSelectedDateRangeValue('all');
      setDateRange({ startDate: null, endDate: null });
    } else {
      setSelectedDateRangeValue('Last 30 Days');
      setDateRange({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date()
      });
    }
    setShowDateRange(false);
    onReset?.();
    onFiltersChange({});
  };

  // Handle date change
  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        const s = { ...dateRange };
        s.startDate = newValue;
        setDateRange(s);
      } else if (name === 'endDate') {
        const s = { ...dateRange };
        s.endDate = newValue;
        setDateRange(s);
      }
    }
  };

  // Handle date range selection
  // Filters owns the presets and the custom pickers; this just reads start/end back out.
  const handleDateFilter = (queryString: string) => {
    const params = new URLSearchParams(String(queryString).replace(/^&/, ''));
    const start = params.get('start');
    const end = params.get('end');
    if (start && end) {
      setDateRange({ startDate: new Date(start), endDate: new Date(end) });
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
  };

  // Fetch suppliers on mount
  useEffect(() => {
    const getOptions = async () => {
      if (selectedAccount && modulePermissions && canViewSuppliers) {
        const { _id: id } = selectedAccount;
        const data: any = await dispatch(fetchSuppliers({ accountId: id, params: '' }));

        if (data.payload && data.payload.suppliers && data.payload.suppliers.length > 0) {
          let options = data.payload.suppliers.map((item: any) => ({
            ...item,
            label: item.legalName || item.name,
            value: item._id
          }));
          options.push({ label: 'All Vendors', value: 'all', _id: 'all' });
          setVendorOptions(options);
        }
      }
    };
    getOptions();
  }, [dispatch, selectedAccount, modulePermissions, canViewSuppliers]);

  // Apply filters when they change
  useEffect(() => {
    if (!defaultAllDates && !dateRange.startDate) return;
    createFilter();
  }, [dateRange, selectedVendor, selectedStatus, selectedCategory, selectedPaymentMode, selectedDateRangeValue, defaultAllDates]);

  // Handle vendor change
  const onVendorChangeHandler = (_event: any, value: any) => {
    setVendorSearchValue('');
    setSelectedVendor(value);
  };

  // Handle vendor + employee search
  const onVendorInputChangeHandler = async (_event: React.SyntheticEvent, value: string) => {
    setSelectedVendor(null);
    setVendorSearchValue(value);

    if (value.length > 2) {
      // Search vendors
      const _querystring = `name=${value}&searchFor=supplier`;
      let vendorResults: any[] = [];
      if (canViewSuppliers) try {
        const data = await dispatch(fetchBuyerOrSupplierByName(_querystring)).unwrap();
        if (data?.accounts) {
          vendorResults = data.accounts.map((v: any) => ({
            ...v,
            label: v.legalName || v.name,
            _type: 'vendor'
          }));
        }
      } catch (err) {}

      // Search employees
      let employeeResults: any[] = [];
      if (selectedAccount?._id) {
        try {
          const empData: any = await dispatch(fetchEmployees({
            accountId: selectedAccount._id,
            type: 'employee',
            params: `filter=${value}`
          })).unwrap();
          employeeResults = (empData?.employees || []).map((e: any) => ({
            _id: e._id,
            name: e.name || e.empId,
            label: e.name || e.empId,
            _type: 'employee'
          }));
        } catch (err) {
          console.error('Employee search failed:', err);
        }
      }

      setVendorOptions([...vendorResults, ...employeeResults]);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 10, columnGap: 10 }}>
        <IconButton
          onClick={onResetClickHandler}
          style={{
            color: '#488dff',
            backgroundColor: '#fff',
            border: '2px solid #e9ecf1',
            padding: 6
          }}
          size='small'
        >
          <Refresh />
        </IconButton>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormProvider {...methods}>
          {/* Vendor/Supplier Search - hidden for employee users */}
          {!hideVendorSearch && (
          <Autocomplete
            disablePortal
            id="vendor-search"
            value={selectedVendor || null}
            style={{ width: 250 }}
            onChange={onVendorChangeHandler}
            onInputChange={onVendorInputChangeHandler}
            options={vendorOptions}
            classes={{ paper: classes.paper }}
            getOptionLabel={(option) => option.label || option.name || ''}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <span>{option.label || option.name}</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: option._type === 'employee' ? '#1565c0' : '#49a563', fontWeight: 600 }}>
                  {option._type === 'employee' ? 'Employee' : 'Supplier'}
                </span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                className='c-text-field-3'
                variant="standard"
                name="vendor"
                size='small'
                placeholder="Search employee/supplier name..."
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true
                }}
              />
            )}
          />
          )}

          {/* Category Dropdown - Using Grouped Select */}
          <Box sx={{ width: 190 }}>
            <GroupedCategorySelect
              name="categoryFilter"
              label=""
              id="categoryFilter"
              categories={categories}
              selectedValue={selectedCategory === 'all' ? 'all' : selectedCategory}
              onChange={(event: SelectChangeEvent<any>) => {
                setSelectedCategory(event.target.value || 'all');
              }}
              showNoneLabel={true}
              isFilter={true}
              allowCreate={false}
              sx={{
                height: 38,
                '& .MuiInputLabel-root': { 
                  display: 'none' 
                },
                '& .MuiOutlinedInput-root': {
                  height: 38,
                  border: 0,
                  '& .MuiSelect-select': { 
                    padding: '8px 12px',
                    fontSize: '0.875rem'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 0 }
              }}
              className='c-text-field-3'
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
            />
          </Box>

          {/* Status Dropdown */}
          <Select
            name="status"
            disableUnderline
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1.5px solid #e9ecf1",
                  boxShadow: 'none',
                  borderRadius: '5px'
                }
              }
            }}
            sx={{
              boxShadow: "none",
              width: 120,
              height: 38,
              '& .MuiOutlinedInput-input': { p: 0, m: 0 },
              '& .MuiSelect-select': { p: '8px 12px' },
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
            }}
            className='c-text-field-3'
            variant="outlined"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {statusOptions.map(option => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {/* Payment Mode Dropdown */}
          <Select
            name="paymentMode"
            disableUnderline
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1.5px solid #e9ecf1",
                  boxShadow: 'none',
                  borderRadius: '5px'
                }
              }
            }}
            sx={{
              boxShadow: "none",
              width: 135,
              height: 38,
              '& .MuiOutlinedInput-input': { p: 0, m: 0 },
              '& .MuiSelect-select': { p: '8px 12px' },
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
            }}
            className='c-text-field-3'
            variant="outlined"
            value={selectedPaymentMode}
            onChange={(e) => setSelectedPaymentMode(e.target.value)}
          >
            {paymentModeOptions.map(option => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {/* Date Range Dropdown */}
          {/* Date range presets/pickers come from the shared Filters bar. */}
          <Filters
            type="expenses"
            inline
            hidePartySearch
            hideReset
            noPersist
            dateOnly={defaultAllDates}
            filterHandler={handleDateFilter}
          />
          </FormProvider>
        </LocalizationProvider>
        {/* Custom Date Range Pickers */}
        {showDateRange && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '10px', flexShrink: 0 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <FormProvider {...methods}>
                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: 14,
                      height: 38
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 140 }}
                      size='small'
                      placeholder="Start Date"
                    />
                  )}
                  value={dateRange.startDate}
                  onChange={(value) => handleDateChange(value, 'startDate')}
                />

                <DesktopDatePicker
                  inputFormat="dd/MM/yyyy"
                  InputProps={{
                    disableUnderline: true,
                    style: {
                      fontSize: 14,
                      height: 38
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      className='c-text-field-3'
                      variant="standard"
                      sx={{ width: 140 }}
                      size='small'
                      placeholder="End Date"
                    />
                  )}
                  value={dateRange.endDate}
                  onChange={(value) => handleDateChange(value, 'endDate')}
                />
              </FormProvider>
            </LocalizationProvider>
          </div>
        )}
      </div>

    </>
  );
};

export default ExpenseFilter;
